import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

// Fetal anatomy fields split into two columns for layout
const ANATOMY_FIELDS_LEFT = [
  ['spine', 'Spine'],
  ['skull', 'Skull'],
  ['brain', 'Brain'],
  ['face', 'Face'],
  ['lips', 'Lips'],
  ['chest', 'Chest'],
  ['heart4CH', '4-Chamber heart'],
  ['heartOutflow', 'Outflow tracts'],
  ['diaphragm', 'Diaphragm'],
  ['abdominalWall', 'Abdominal wall'],
  ['stomach', 'Stomach'],
  ['bowel', 'Bowel'],
  ['liver', 'Liver'],
  ['kidneys', 'Kidneys'],
  ['bladder', 'Bladder'],
];

const ANATOMY_FIELDS_RIGHT = [
  ['umbilicalCordInsertion', 'Cord insertion'],
  ['upperExtremities', 'Upper extremities'],
  ['lowerExtremities', 'Lower extremities'],
  ['hands', 'Hands'],
  ['feet', 'Feet'],
  ['fingers', 'Fingers'],
  ['toes', 'Toes'],
  ['nuchalFold', 'Nuchal fold'],
  ['cavumSeptumPellucidum', 'CSP'],
  ['cerebellum', 'Cerebellum'],
  ['choroidPlexus', 'Choroid plexus'],
  ['lateralVentricles', 'Lateral ventricles'],
  ['cisternaMagna', 'Cisterna magna'],
  ['nose', 'Nose'],
  ['ears', 'Ears'],
  ['genitalia', 'Genitalia'],
];

export function FollowUpOBPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const bio = data.biometry || {};
  const ga = data.gestationalAge || {};
  const ms = data.maternalStructures || {};
  const bpp = data.biophysicalProfile || {};
  const uaDoppler = data.uaDoppler || {};
  const anatomy = data.anatomy || {};

  const measurementsText = [
    bio.bpd ? `BPD: ${bio.bpd} cm` : null,
    bio.hc ? `HC: ${bio.hc} cm` : null,
    bio.ac ? `AC: ${bio.ac} cm` : null,
    bio.fl ? `FL: ${bio.fl} cm` : null,
    bio.efw ? `EFW: ${bio.efw} g` : null,
    bio.percentile ? `Percentile: ${bio.percentile}` : null,
    data.afi ? `AFI: ${data.afi} cm` : null,
    data.cervixLength && !data.cervixNotVisualized ? `Cervix length: ${data.cervixLength} cm` : null,
    data.fetalHeartRate ? `FHR: ${data.fetalHeartRate} bpm` : null,
  ].filter(Boolean).join('\n');

  return (
    <Document>
      {/* Page 1: Fetal biometry and status */}
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="FOLLOW-UP OB ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="BIOMETRY">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>

            <View style={commonStyles.measurementBox}>
              <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>GESTATIONAL AGE</Text>
              {ga.lmp && <Text style={commonStyles.infoBoxText}>LMP: {ga.lmp}</Text>}
              {ga.clinicalEGA && <Text style={commonStyles.infoBoxText}>Clinical EGA: {ga.clinicalEGA}</Text>}
              {ga.clinicalEDD && <Text style={commonStyles.infoBoxText}>Clinical EDD: {ga.clinicalEDD}</Text>}
              {ga.ultrasoundEGA && <Text style={commonStyles.infoBoxText}>US EGA: {ga.ultrasoundEGA}</Text>}
              {ga.ultrasoundEDD && <Text style={commonStyles.infoBoxText}>US EDD: {ga.ultrasoundEDD}</Text>}
            </View>

            {(bpp.breathing !== undefined || bpp.movement !== undefined) && (
              <View style={commonStyles.measurementBox}>
                <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>BPP</Text>
                {bpp.breathing !== undefined && <Text style={commonStyles.infoBoxText}>Breathing: {bpp.breathing}/2</Text>}
                {bpp.movement !== undefined && <Text style={commonStyles.infoBoxText}>Movement: {bpp.movement}/2</Text>}
                {bpp.tone !== undefined && <Text style={commonStyles.infoBoxText}>Tone: {bpp.tone}/2</Text>}
                {bpp.fluid !== undefined && <Text style={commonStyles.infoBoxText}>Fluid: {bpp.fluid}/2</Text>}
              </View>
            )}

            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="FETAL STATUS" />
            {data.fetalHeartRate && (
              <PdfLabelValue label="Fetal heart rate:" value={`${data.fetalHeartRate} bpm`}
                isAbnormal={parseFloat(data.fetalHeartRate) < 110 || parseFloat(data.fetalHeartRate) > 160} />
            )}
            <PdfLabelValue label="Presentation:" value={data.presentation || ''} forceBlack />
            {data.numberOfFetuses && <PdfLabelValue label="Number of fetuses:" value={data.numberOfFetuses} forceBlack />}
            {data.gender && <PdfLabelValue label="Gender:" value={data.gender} forceBlack />}

            <PdfAccentSectionHeader title="PLACENTA / FLUID" />
            <PdfLabelValue label="Placenta location:" value={data.placentaLocation || ''} forceBlack />
            <PdfLabelValue label="Placenta grade:" value={data.placentaGrade || ''} forceBlack />
            <PdfLabelValue label="Placenta previa:" value={data.placentaPrevia || 'None'} isAbnormal={data.placentaPrevia && data.placentaPrevia !== 'None'} />
            <PdfLabelValue label="Umbilical cord:" value={data.umbilicalCord || ''} isAbnormal={data.umbilicalCord && data.umbilicalCord !== 'Normal'} />
            <PdfLabelValue label="Amniotic fluid:" value={data.amnioticFluid || ''} isAbnormal={data.amnioticFluid && data.amnioticFluid !== 'Normal'} />

            <PdfAccentSectionHeader title="UA DOPPLER" />
            {uaDoppler.fetal && <PdfLabelValue label="Fetal end:" value={uaDoppler.fetal} isAbnormal={uaDoppler.fetal !== 'Normal'} />}
            {uaDoppler.midCord && <PdfLabelValue label="Mid-cord:" value={uaDoppler.midCord} isAbnormal={uaDoppler.midCord !== 'Normal'} />}
            {uaDoppler.placenta && <PdfLabelValue label="Placental end:" value={uaDoppler.placenta} isAbnormal={uaDoppler.placenta !== 'Normal'} />}

            <PdfAccentSectionHeader title="MATERNAL STRUCTURES" />
            {data.cervixNotVisualized && <PdfLabelValue label="Cervix:" value="Not well visualized" isAbnormal />}
            {data.cervixFunneling && <PdfLabelValue label="Cervical funneling:" value="Present" isAbnormal />}
            <PdfLabelValue label="Uterus:" value={ms.uterus || ''} isAbnormal={ms.uterus && ms.uterus !== 'Normal'} />
            {ms.priorCSection && <PdfLabelValue label="Prior C-section:" value="Yes" forceBlack />}
            {ms.placentaScarDistance && <PdfLabelValue label="Placenta-scar distance:" value={ms.placentaScarDistance} forceBlack />}
            <PdfLabelValue label="Right ovary:" value={ms.rightOvary || ''} isAbnormal={ms.rightOvary && ms.rightOvary !== 'Normal'} />
            <PdfLabelValue label="Left ovary:" value={ms.leftOvary || ''} isAbnormal={ms.leftOvary && ms.leftOvary !== 'Normal'} />
            {ms.ovarianMasses && ms.ovarianMasses.length > 0 && (
              <PdfLabelValue label="Ovarian masses:" value={ms.ovarianMasses.filter(Boolean).join(', ')} isAbnormal />
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>

      {/* Page 2: Fetal anatomy */}
      {Object.keys(anatomy).length > 0 && (
        <Page size="LETTER" style={commonStyles.page}>
          <PdfPageHeader facilityInfo={facilityInfo} examTitle="FOLLOW-UP OB — FETAL ANATOMY" />
          <PdfPatientBox
            firstName={data.firstName}
            lastName={data.lastName}
            mrn={data.mrn}
            examDate={data.examDate}
            technologist={data.technologist}
            studentWorksheet={data.studentWorksheet}
            supervisingSonographer={data.supervisingSonographer}
          />

          <View style={commonStyles.twoCol}>
            <View style={commonStyles.colLeft}>
              <PdfAccentSectionHeader title="ANATOMY (1 of 2)" />
              {ANATOMY_FIELDS_LEFT.map(([key, label]) =>
                anatomy[key] ? (
                  <PdfLabelValue key={key} label={`${label}:`} value={anatomy[key]} isAbnormal={anatomy[key] !== 'Normal'} />
                ) : null
              )}
            </View>
            <View style={commonStyles.colRight}>
              <PdfAccentSectionHeader title="ANATOMY (2 of 2)" />
              {ANATOMY_FIELDS_RIGHT.map(([key, label]) =>
                anatomy[key] ? (
                  <PdfLabelValue key={key} label={`${label}:`} value={anatomy[key]} isAbnormal={anatomy[key] !== 'Normal'} />
                ) : null
              )}
            </View>
          </View>

          <PdfFooter facilityInfo={facilityInfo} />
        </Page>
      )}
    </Document>
  );
}
